"use client";

import {
  VALUE_PROP_DEMO_APPROACH_MS,
  VALUE_PROP_DEMO_PRESS_MS,
  VALUE_PROP_DEMO_RELEASE_MS,
  type ValuePropDemoClickPhase,
} from "@/components/about/value-prop-utils";
import type { CSSProperties } from "react";

type ValuePropDemoCursorProps = {
  phase: Exclude<ValuePropDemoClickPhase, "idle">;
};

function animationForPhase(phase: ValuePropDemoCursorProps["phase"]): string {
  if (phase === "approach") {
    return `value-prop-demo-cursor-in ${VALUE_PROP_DEMO_APPROACH_MS}ms cubic-bezier(0.34, 1.45, 0.64, 1) both`;
  }
  if (phase === "press") {
    return `value-prop-demo-cursor-press ${VALUE_PROP_DEMO_PRESS_MS}ms cubic-bezier(0.2, 0.9, 0.3, 1) both`;
  }
  return `value-prop-demo-cursor-out ${VALUE_PROP_DEMO_RELEASE_MS}ms ease-in both`;
}

/**
 * Fake pointer that pops onto an icon and “clicks” it during auto-rotate,
 * so the radiogroup reads as interactive before the user touches anything.
 */
export function ValuePropDemoCursor({ phase }: ValuePropDemoCursorProps) {
  const style = { animation: animationForPhase(phase) } as CSSProperties;

  return (
    <span
      data-testid="value-prop-demo-cursor"
      data-phase={phase}
      aria-hidden="true"
      // Outer keeps anchor position; inner owns animated transforms so they don't clash.
      // Tip (SVG top-left) sits on the icon’s bottom-right corner.
      className="pointer-events-none absolute z-20 right-[-20%] bottom-[-16%] opacity-100"
    >
      <span className="block drop-shadow-[0_3px_6px_rgba(0,0,0,0.4)]" style={style}>
        {/* Simple tailless arrow tip: black fill, white outline (~2× prior size). */}
        <svg
          width="72"
          height="72"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="h-[4.5rem] w-[4.5rem] sm:h-20 sm:w-20"
          focusable="false"
        >
          <title>Demo pointer</title>
          <path
            d="M5 3.2 L5 18.5 L10.2 13.8 L18.2 13.5 Z"
            fill="#111111"
            stroke="white"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </span>
  );
}
